import { Injectable } from '@angular/core';
import { ViolationStateServiceTsService } from './violation-state.service.ts.service';
import * as faceapi from 'face-api.js';
import { ProctoringRule } from '../models/rule.model';



@Injectable({
	providedIn: 'root'
})
export class ProctoringRuleEngineServiceTsService {

	private _screenStream: MediaStream | null = null;

	private counters: Record<string, number> = {
		NO_FACE: 0,
		MULTIPLE_FACES: 0,
		LOOKING_AWAY: 0,
		EYES_CLOSED: 0,
		FACE_TOO_FAR: 0
	};

	private FPS = 6;
	private LIMITS: Record<string, number> = {
		NO_FACE: 3 * 6,
		MULTIPLE_FACES: 1 * 6,
		LOOKING_AWAY: 3 * 6,
		EYES_CLOSED: 1 * 6,
		FACE_TOO_FAR: 3 * 6
	};

	private severityMap: Record<string, 'CRITICAL' | 'WARNING'> = {
		NO_FACE: 'WARNING',
		MULTIPLE_FACES: 'WARNING',
		LOOKING_AWAY: 'WARNING',
		EYES_CLOSED: 'WARNING',
		FACE_TOO_FAR: 'WARNING',
		TAB_SWITCH: 'CRITICAL',
		BACKGROUND_VOICE: 'WARNING'
	};


	constructor(private violationState: ViolationStateServiceTsService) { }


	evaluate(detections: any[]): void {
		if (this.violationState.snapshot.isTerminated) return;

		const videoHeight = 480;

		if (detections.length === 0) {
			this.increment('NO_FACE');
		} else {
			this.counters['NO_FACE'] = 0;
		}

		const significantFaces = detections.filter(
			(d: any) => d.detection.box.height > videoHeight * 0.05);
		if (significantFaces.length > 1) {
			this.increment('MULTIPLE_FACES');
		} else {
			this.counters['MULTIPLE_FACES'] = Math.max(0, this.counters['MULTIPLE_FACES'] - 1);
		}

		if (detections.length === 1) {
			const det = detections[0];
			const landmarks = det.landmarks;

			if (this.isLookingAway(landmarks)) {
				this.increment('LOOKING_AWAY');
			} else {
				if (this.counters['LOOKING_AWAY'] > 0) this.counters['LOOKING_AWAY']--;
			}

			if (this.isEyesClosed(landmarks)) {
				this.counters['EYES_CLOSED']++;
			} else {
				this.counters['EYES_CLOSED'] = Math.max(0, this.counters['EYES_CLOSED'] - 2);
			}

			if (this.counters['EYES_CLOSED'] >= this.LIMITS['EYES_CLOSED']) {
				this.counters['EYES_CLOSED'] = 0;
				this.violationState.trigger(
					'EYES_CLOSED',
					this.severityMap['EYES_CLOSED']
				);
			}

			if (this.isFaceTooFar(det.detection)) {
				this.increment('FACE_TOO_FAR');
			} else {
				if (this.counters['FACE_TOO_FAR'] > 0) this.counters['FACE_TOO_FAR']--;
			}
		}
	}


	private increment(type: string): void {
		this.counters[type]++;
		if (this.counters[type] >= this.LIMITS[type]) {

			this.counters[type] = 0;
			this.violationState.trigger(type as any, this.severityMap[type]);
		}
	}


	private isLookingAway(landmarks: faceapi.FaceLandmarks68): boolean {
		const nose = landmarks.getNose();
		const jaw = landmarks.getJawOutline();

		if (!nose?.length || !jaw?.length) return false;
		const noseTip = nose[3];
		const leftEdge = jaw[0];
		const rightEdge = jaw[jaw.length - 1];
		const distLeft = noseTip.x - leftEdge.x;
		const distRight = rightEdge.x - noseTip.x;

		if (distRight === 0) return false;
		const ratio = distLeft / distRight;
		return ratio > 2.5 || ratio < 0.4;
	}


	private isEyesClosed(landmarks: faceapi.FaceLandmarks68): boolean {
		const leftEye = landmarks.getLeftEye();
		const rightEye = landmarks.getRightEye();

		const ear = (eye: faceapi.Point[]) => {
			const v1 = Math.hypot(eye[1].x - eye[5].x, eye[1].y - eye[5].y);
			const v2 = Math.hypot(eye[2].x - eye[4].x, eye[2].y - eye[4].y);
			const h = Math.hypot(eye[0].x - eye[3].x, eye[0].y - eye[3].y);
			return (v1 + v2) / (2 * h);
		};

		const leftEAR = ear(leftEye);
		const rightEAR = ear(rightEye);
		const avgEAR = (leftEAR + rightEAR) / 2;
		return avgEAR < 0.22;
	}


	private isFaceTooFar(detection: faceapi.FaceDetection): boolean {
		const ratio = detection.box.height / 480;
		return ratio < 0.35;
	}



	setRules(rules: ProctoringRule[]): void {
		rules.forEach(rule => {
			if (this.LIMITS[rule.type] !== undefined) {
				this.LIMITS[rule.type] = rule.debounce_seconds * this.FPS;
				if (rule.severity === 'CRITICAL' || rule.severity === 'WARNING') {
					this.severityMap[rule.type] = rule.severity;
				}
			}
		});
	}

	getSeverity(type: string): 'CRITICAL' | 'WARNING' {
		return this.severityMap[type] || 'WARNING';
	}


	setScreenStream(stream: MediaStream): void {
		this._screenStream = stream;
	}

	getScreenStream(): MediaStream | null {
		return this._screenStream;
	}

	clearScreenStream(): void {
		this._screenStream?.getTracks().forEach(t => t.stop());
		this._screenStream = null;
	}


}